/**
 * Pitch recording flow: editor timeline is built from pitch project segments, not story scenes.
 */

import {
  MIN_CLIP_PLAYBACK_SEC,
  clampVideoClipTrimToMediaCap,
  clipHasUserVideoTrim,
  clipVideoSnippetDrawDuration,
} from './videoEditorPlayback';

export function isPitchRecordingStory(story) {
  return String(story?.story_type || '').toLowerCase() === 'pitch_recording';
}

export function isPitchRecordingEditorScenes(scenes) {
  return Boolean((scenes || []).some((s) => s?.pitch_recording_segment));
}

/** True for pitch takes — video length is fixed; never offer AI extend-to-audio. */
export function isPitchTimelineClip(videoClip, audioClip = null) {
  if (videoClip?.pitchRecording || videoClip?.sourceType === 'pitch_segment') return true;
  if (audioClip?.sourceType === 'pitch_segment') return true;
  return false;
}

/** Best-known recorded length for a pitch take (probe, scene row, or saved clip). */
export function pitchRecordingDurationSeconds(clip, scene = null) {
  const fromScene = scene
    ? Math.max(
      Number(scene.video_duration_seconds) || 0,
      Number(scene.duration_seconds) || 0,
    )
    : 0;
  return Math.max(
    fromScene,
    Number(clip?.videoMediaDurationSeconds) || 0,
    Number(clip?.videoSourceDurationMax) || 0,
    Number(clip?.trimEnd) || 0,
    Number(clip?.duration) || 0,
  );
}

/** After metadata probe: lock pitch caps to real file length; keep user trim when present. */
export function pitchVideoClipAfterProbe(clip, probedSeconds, scene = null) {
  const probed = Number(probedSeconds);
  const fallback = pitchRecordingDurationSeconds(clip, scene);
  const d = probed > 0 && Number.isFinite(probed) ? probed : fallback;
  if (!clip?.videoUrl || !(d > 0)) return clip;
  const base = {
    ...clip,
    pitchRecording: true,
    sourceType: clip.sourceType || 'pitch_segment',
    videoMediaDurationSeconds: d,
    videoSourceDurationMax: d,
  };
  if (clipHasUserVideoTrim(clip, d)) {
    return syncPitchClipTimelineMetadata(clampVideoClipTrimToMediaCap(base));
  }
  return {
    ...base,
    trimStart: 0,
    trimEnd: d,
    duration: d,
    timelineSlotDuration: d,
    videoPlaybackSpeed: 1,
    playbackDuration: d,
  };
}

/** Merge a pitch take clip without narration-style trim/extend rules. */
export function mergePitchTimelineClipWithScene(clip, scene, extra = {}) {
  const videoDur = Math.max(pitchRecordingDurationSeconds(clip, scene), 0.25);
  const base = {
    ...clip,
    pitchRecording: true,
    sourceType: clip?.sourceType || 'pitch_segment',
    videoMediaDurationSeconds: Math.max(Number(clip?.videoMediaDurationSeconds) || 0, videoDur),
    videoSourceDurationMax: Math.max(Number(clip?.videoSourceDurationMax) || 0, videoDur),
    ...extra,
  };
  if (clipHasUserVideoTrim(clip, videoDur)) {
    return syncPitchClipTimelineMetadata(clampVideoClipTrimToMediaCap(base));
  }
  return {
    ...base,
    duration: videoDur,
    timelineSlotDuration: videoDur,
    trimStart: 0,
    trimEnd: videoDur,
    videoPlaybackSpeed: 1,
    playbackDuration: videoDur,
  };
}

export function tagPitchRecordingVideoTracks(videoTracks, story) {
  if (!isPitchRecordingStory(story)) return videoTracks;
  return (videoTracks || []).map((track) =>
    (track || []).map((clip) => {
      if (!clip?.videoUrl) return clip;
      return syncPitchClipTimelineMetadata({
        ...clip,
        pitchRecording: true,
        sourceType: clip.sourceType || 'pitch_segment',
      });
    }),
  );
}

/** Keep pitch clip metadata aligned with visible trim after resize (drag snap uses lane duration). */
export function syncPitchClipTimelineMetadata(clip) {
  if (!isPitchTimelineClip(clip)) return clip;
  const visible = clipVideoSnippetDrawDuration(clip);
  const trimEnd = Number(clip.trimEnd ?? clip.duration) || visible;
  const trimStart = Math.max(0, Number(clip.trimStart) || 0);
  const trimSpan = Math.max(MIN_CLIP_PLAYBACK_SEC, trimEnd - trimStart);
  return {
    ...clip,
    pitchRecording: true,
    sourceType: clip.sourceType || 'pitch_segment',
    duration: trimSpan,
    timelineSlotDuration: visible,
    playbackDuration: visible,
  };
}

/** One pseudo-scene per recorded pitch segment (matches clip sceneId linkage). */
export function pitchSegmentToEditorScene(segment, index = 0) {
  const rec = segment?.recording || {};
  const sid = String(segment?.id || '');
  const audioDur = Number(rec.duration_sec ?? segment?.target_duration_sec) || 10;
  const videoDur = rec.video_url
    ? Number(rec.video_duration_sec ?? rec.duration_sec ?? audioDur) || audioDur
    : 0;
  const text = String(
    segment?.script_text || segment?.teleprompter_text || segment?.title || '',
  ).trim();

  return {
    id: sid,
    scene_number: Number(segment?.order) || index + 1,
    title: segment?.title || `Part ${segment?.order || index + 1}`,
    scene_text: text,
    narration_text: text,
    video_url: rec.video_url || null,
    image_url: null,
    audio_url: rec.audio_url || null,
    duration_seconds: audioDur,
    video_duration_seconds: videoDur > 0 ? videoDur : null,
    pitch_recording_segment: true,
    video_s3_url: rec.video_s3_url || null,
    video_local_url: rec.video_local_url || null,
    audio_s3_url: rec.audio_s3_url || null,
    audio_local_url: rec.audio_local_url || null,
  };
}

export function pitchSegmentsToEditorScenes(segments) {
  const ordered = [...(segments || [])].sort(
    (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0),
  );
  return ordered
    .filter((seg) => String((seg?.recording || {}).audio_url || '').trim())
    .map((seg, index) => pitchSegmentToEditorScene(seg, index));
}

/**
 * Load editor scene rows for a pitch story. Returns null when the story is not pitch-based.
 */
export async function loadPitchStoryEditorScenes(api, story) {
  if (!isPitchRecordingStory(story)) return null;
  const projectId = String(story?.pitch_project_id || '').trim();
  if (!projectId) return [];
  const { data } = await api.get(`/pitch/projects/${projectId}`, {
    params: { _editorFresh: Date.now() },
  });
  return pitchSegmentsToEditorScenes(data?.project?.segments);
}
